import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDarkMode, useDevMode } from "../contexts/ThemeContext";
import { useDataCache } from "../contexts/DataCacheContext";
import config from "../config";

// format bytes to human-readable string
const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

// color based on severity
const badgeStyle = (level, darkMode) => {
  const colors = {
    green:  { bg: darkMode ? "#1a3d1a" : "#d1ecf1", text: darkMode ? "#66bb6a" : "#0c5460" },
    yellow: { bg: darkMode ? "#5c4a1a" : "#fff3cd", text: darkMode ? "#ffc107" : "#856404" },
    red:    { bg: darkMode ? "#5c1a1a" : "#f8d7da", text: darkMode ? "#ff6b6b" : "#721c24" },
  };
  return colors[level] || colors.green;
};

// measure all vrtta_cache entries in localStorage
const measureLocalStorageCache = () => {
  let totalBytes = 0;
  let entryCount = 0;
  const entries = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("vrtta_cache")) {
        const val = localStorage.getItem(key);
        const size = (key.length + (val?.length || 0)) * 2; // UTF-16 estimate
        totalBytes += size;
        entryCount++;
        const parts = key.split("_");
        const name = parts[parts.length - 1] || key;
        entries.push({ name, size, source: "disk" });
      }
    }
  } catch (_) {
    // localStorage inaccessible
  }

  return { totalBytes, entryCount, entries };
};

const MemoryMonitor = ({ darkMode }) => {
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const intervalRef = useRef(null);
  const peakHeapRef = useRef(0);
  const location = useLocation();
  const { getCacheMemoryStats } = useDataCache();

  const collect = useCallback(() => {
    // Chrome heap (optional — not available on Firefox/Safari)
    const heap = performance?.memory
      ? { used: performance.memory.usedJSHeapSize, total: performance.memory.jsHeapSizeLimit }
      : null;

    if (heap) peakHeapRef.current = Math.max(peakHeapRef.current, heap.used);

    // cross-browser: DOM element count (excludes shadow DOM, includes MUI style tags)
    const domNodes = document.querySelectorAll("*").length;

    // localStorage cache
    const diskCache = measureLocalStorageCache();

    // in-memory cache from DataCacheContext ref
    let memCache = { totalBytes: 0, entryCount: 0, entries: [] };
    try {
      memCache = getCacheMemoryStats();
      // tag entries as "mem"
      memCache.entries = memCache.entries.map((e) => ({ ...e, source: "mem" }));
    } catch (_) {
      // context not ready
    }

    // combined total
    const combinedCacheBytes = diskCache.totalBytes + memCache.totalBytes;

    setStats({
      heap,
      peakHeap: peakHeapRef.current,
      domNodes,
      diskCache,
      memCache,
      combinedCacheBytes,
    });
  }, [getCacheMemoryStats]);

  // refresh on mount + every route change
  useEffect(() => {
    collect();
  }, [location.pathname, collect]);

  // poll only while expanded
  useEffect(() => {
    if (expanded) {
      collect();
      intervalRef.current = setInterval(collect, 3000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [expanded, collect]);

  if (!stats) return null;

  const { heap, peakHeap, domNodes, diskCache, memCache, combinedCacheBytes } = stats;

  // DOM node severity
  const domLevel = domNodes > 5000 ? "red" : domNodes > 2500 ? "yellow" : "green";
  // combined cache severity
  const cacheMB = combinedCacheBytes / (1024 * 1024);
  const cacheLevel = cacheMB > 3 ? "red" : cacheMB > 1 ? "yellow" : "green";

  const pill = (label, value, level) => {
    const c = badgeStyle(level, darkMode);
    return (
      <Box
        key={label}
        sx={{
          backgroundColor: c.bg,
          color: c.text,
          padding: "1px 7px",
          borderRadius: "4px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {label}: {value}
      </Box>
    );
  };

  // merge disk + mem entries, dedup by name (prefer mem if both exist)
  const mergedEntries = () => {
    const map = new Map();
    for (const e of diskCache.entries) {
      map.set(e.name, { ...e, diskSize: e.size, memSize: 0 });
    }
    for (const e of memCache.entries) {
      const existing = map.get(e.name);
      if (existing) {
        existing.memSize = e.size;
        existing.size = existing.diskSize + e.size;
      } else {
        map.set(e.name, { ...e, diskSize: 0, memSize: e.size });
      }
    }
    return [...map.values()].sort((a, b) => b.size - a.size);
  };

  return (
    <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "10px",
          fontFamily: "'Poppins', monospace",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {heap && (() => {
          const usedMB = heap.used / (1024 * 1024);
          const heapLevel = usedMB > 200 ? "red" : usedMB > 100 ? "yellow" : "green";
          return pill("Heap", formatBytes(heap.used), heapLevel);
        })()}
        {pill("DOM", domNodes.toLocaleString(), domLevel)}
        {pill("Cache", formatBytes(combinedCacheBytes), cacheLevel)}
        <Box sx={{ opacity: 0.5, fontSize: "9px" }}>{expanded ? "▲ live" : "▼"}</Box>
      </Box>

      {/* Expanded detail dropdown */}
      {expanded && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: "6px",
            backgroundColor: darkMode ? "#1e1e1e" : "#fff",
            border: `1px solid ${darkMode ? "#333" : "#ddd"}`,
            borderRadius: "8px",
            padding: "10px 14px",
            fontSize: "10px",
            fontFamily: "'Poppins', monospace",
            color: darkMode ? "#ccc" : "#333",
            zIndex: 9999,
            minWidth: "280px",
            boxShadow: darkMode
              ? "0 4px 20px rgba(0,0,0,0.5)"
              : "0 4px 20px rgba(0,0,0,0.12)",
          }}
        >
          <Box sx={{ fontWeight: 700, marginBottom: "6px", fontSize: "11px" }}>
            Memory Details
          </Box>

          {/* Heap */}
          {heap ? (
            <Box sx={{ marginBottom: "8px" }}>
              <Box>JS Heap: {formatBytes(heap.used)} / {formatBytes(heap.total)}</Box>
              <Box sx={{ opacity: 0.6 }}>Peak: {formatBytes(peakHeap)}</Box>
            </Box>
          ) : (
            <Box sx={{ marginBottom: "8px", opacity: 0.5, fontStyle: "italic" }}>
              JS Heap: not available (Chrome/Edge only)
            </Box>
          )}

          {/* DOM */}
          <Box sx={{ marginBottom: "8px" }}>
            DOM Nodes: {domNodes.toLocaleString()}
            <Box sx={{ opacity: 0.45, fontStyle: "italic", fontSize: "9px" }}>
              Approximate — includes MUI style tags, excludes shadow DOM
            </Box>
          </Box>

          {/* Cache breakdown */}
          <Box>
            <Box sx={{ fontWeight: 600, marginBottom: "4px" }}>
              App Cache: {formatBytes(combinedCacheBytes)}
            </Box>
            <Box sx={{ display: "flex", gap: "12px", marginBottom: "4px", opacity: 0.6 }}>
              <span>In-memory: {formatBytes(memCache.totalBytes)}</span>
              <span>localStorage: {formatBytes(diskCache.totalBytes)}</span>
            </Box>
            <Box sx={{ opacity: 0.45, fontStyle: "italic", fontSize: "9px", marginBottom: "4px" }}>
              Sizes are UTF-16 estimates — actual browser usage may vary
            </Box>
            {mergedEntries().map((e) => (
              <Box
                key={e.name}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  opacity: 0.7,
                  padding: "1px 0",
                }}
              >
                <span>{e.name}</span>
                <Box sx={{ display: "flex", gap: "6px" }}>
                  {e.memSize > 0 && (
                    <span style={{ opacity: 0.6 }}>mem {formatBytes(e.memSize)}</span>
                  )}
                  {e.diskSize > 0 && (
                    <span style={{ opacity: 0.6 }}>disk {formatBytes(e.diskSize)}</span>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DevModeBar = () => {
  const { darkMode } = useDarkMode();
  const { devModeActions } = useDevMode();

  // don't render anything if not in demo mode
  if (!config.isDemoMode) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: darkMode ? "#1a4d2e" : "#d4edda",
        color: darkMode ? "white" : "#155724",
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        zIndex: 1000,
        fontFamily: "Poppins",
        fontSize: "12px",
        fontWeight: 600,
        borderBottom: darkMode ? "1px solid #669d2e" : "1px solid #c3e6cb",
        flexShrink: 0,
      }}
    >
      {/* Left side: Last updated date + memory monitor */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {config.lastUpdated && (
          <Box sx={{ opacity: 0.8, fontSize: "11px" }}>
            Last Updated: {config.lastUpdated}
          </Box>
        )}
        <MemoryMonitor darkMode={darkMode} />
      </Box>

      {/* Right side: Actions and demo mode label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Render dev action buttons */}
        {devModeActions.map((action, index) => (
          <Button
            key={index}
            variant="contained"
            size="small"
            onClick={action.onClick}
            sx={{
              backgroundColor: darkMode ? "#669d2e" : "#28a745",
              color: "white",
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: darkMode ? "#557a24" : "#218838",
              },
            }}
          >
            {action.label}
          </Button>
        ))}

        <span>DEMO MODE</span>
      </Box>
    </Box>
  );
};

export default DevModeBar;
